import { z } from 'zod'
import { CartSchema } from '../cart/cart'
import { CustomerSchema } from '../customer/customer'

/*
 * Contratos da API de autenticação e cadastro (AuthController e CustomerController da Glofi.API), a partir dos
 * snapshots travados em tests/Glofi.API.Tests/Snapshots/Auth e Customers. Tipos por z.infer. O cliente da sessão é o
 * `CustomerSchema` de `schemas/customer/customer.ts` e o carrinho é o `CartSchema` de `schemas/cart/cart.ts` (o login
 * devolve o mesmo CartDTO do `cart/current`, com outra projeção do produto); a troca de senha do cliente logado é do
 * domínio customer.
 */

// =========================================================================
// SECTION 1: SESSÃO
// =========================================================================

/**
 * Resposta dos logins (`auth/login`, `auth/fast-login`, `auth/custom-login` com sucesso) e do `auth/update-token`
 * (este sem `message`). O cadastro devolve a mesma sessão sem carrinho: `cart` vira null.
 */
export const AuthSessionSchema = z.looseObject({
    token: z.string().min(1),
    /** Cliente: o `CustomerSchema` do domínio customer (fonte única; auth importa de customer). */
    customer: CustomerSchema,
    /**
     * Carrinho: o `CartSchema` do domínio cart. Ilegível vira null em vez de recusar a sessão: o login não pode falhar
     * pelo carrinho, que o `useCart` revalida pelo `cart/current`.
     */
    cart: CartSchema.nullish()
        .catch(null)
        .transform((value) => value ?? null),
    message: z.string().optional(),
    /** Só no acesso de suporte (login-as-customer). */
    supportAccess: z.boolean().optional(),
})

/** `GET auth/login-as-customer`: a sessão do cliente aberta pelo atendente, com validade própria em segundos. */
export const SupportLoginResponseSchema = AuthSessionSchema.extend({
    expiration: z.number(),
    supportAccess: z.literal(true),
})

/**
 * `POST auth/custom-login` (e-mail do carrinho, cliente sem conta). Responde 200 também nos erros: `status: "error"`
 * com `email` quando o e-mail já tem conta ("Usuário já cadastrado"), ou com a mensagem de falha na criação.
 */
export const GuestLoginResponseSchema = z.discriminatedUnion('status', [
    AuthSessionSchema.extend({ status: z.literal('success') }),
    z.looseObject({ status: z.literal('error'), message: z.string(), email: z.string().optional() }),
])

/** `POST customer/create` e `customer/create-company-customer`: token e cliente, sem carrinho. */
export const RegisterResponseSchema = AuthSessionSchema

/** `GET auth/change-password` com o token do link: o link é válido e pertence a este e-mail. */
export const RecoveryTokenResponseSchema = z.looseObject({
    email: z.string(),
    message: z.string().optional(),
})

/** Respostas em texto puro: `auth/request-password-link*` e `PUT auth/change-password`. */
export const TextMessageSchema = z.string()

export type IAuthSession = z.infer<typeof AuthSessionSchema>
export type ISupportLoginResponse = z.infer<typeof SupportLoginResponseSchema>
export type IGuestLoginResponse = z.infer<typeof GuestLoginResponseSchema>
export type IRegisterResponse = z.infer<typeof RegisterResponseSchema>
export type IRecoveryTokenResponse = z.infer<typeof RecoveryTokenResponseSchema>

// =========================================================================
// SECTION 2: CORPOS DE REQUISIÇÃO
// =========================================================================

/** `POST auth/login` (LoginRequest). */
export const LoginRequestSchema = z.object({ email: z.string(), password: z.string() })

/** `POST auth/custom-login` (OauthUserDTO): a loja manda nome e token vazios. */
export const GuestLoginRequestSchema = z.object({ name: z.string(), email: z.string(), token: z.string() })

/** `POST customer/create` (CreateCustomerRequest): telefone e CPF sem máscara. */
export const RegisterPersonRequestSchema = z.object({
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string(),
    cpf: z.string(),
    occupation: z.string(),
    password: z.string(),
    confirmPassword: z.string(),
})

/** `POST customer/create-company-customer` (CreateCompanhyCustomerRequest): CNPJ alfanumérico em maiúsculas. */
export const RegisterCompanyRequestSchema = z.object({
    email: z.string(),
    corporateName: z.string(),
    companyPhone: z.string(),
    cnpj: z.string(),
    occupation: z.string(),
    inscricaoMunicipal: z.string(),
    inscricaoEstadual: z.string(),
    password: z.string(),
    confirmPassword: z.string(),
})

/** `POST auth/request-password-link` e `auth/request-password-link-by-maindoc` (documento sem máscara). */
export const ForgotPasswordByEmailRequestSchema = z.object({ email: z.string() })
export const ForgotPasswordByDocumentRequestSchema = z.object({ MainDocument: z.string() })

/** `PUT auth/change-password` (UpdatePasswordRequest), com o token do link no Authorization. */
export const RecoverPasswordRequestSchema = z.object({ newPassword: z.string(), newPasswordConfirm: z.string() })

export type ILoginRequest = z.infer<typeof LoginRequestSchema>
export type IGuestLoginRequest = z.infer<typeof GuestLoginRequestSchema>
export type IRegisterPersonRequest = z.infer<typeof RegisterPersonRequestSchema>
export type IRegisterCompanyRequest = z.infer<typeof RegisterCompanyRequestSchema>
export type IForgotPasswordByEmailRequest = z.infer<typeof ForgotPasswordByEmailRequestSchema>
export type IForgotPasswordByDocumentRequest = z.infer<typeof ForgotPasswordByDocumentRequestSchema>
export type IRecoverPasswordRequest = z.infer<typeof RecoverPasswordRequestSchema>
